import { useMemo, useState, type ReactNode } from 'react';
import { LuCircleCheckBig } from 'react-icons/lu';
import { MdVisibility } from 'react-icons/md';
import clsx from 'clsx';
import type { AgentApiAnalysisSharedSummaryItem } from '@/types/serializers';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { ANALYSIS_STEPS, TABLE_STEPS } from '../constants';

interface AnalysisSummaryCardsProps {
  /** APIレスポンスのサマリーデータ（API 側のステップキーで返る） */
  summary: AgentApiAnalysisSharedSummaryItem[];
  /** 現在表示中の API ステップキー配列（URLの visible_steps） */
  visibleSteps: string[];
  /** 表示ステップ変更時のコールバック */
  onVisibleStepsChange: (steps: string[]) => void;
  /** メトリクス種別（UUか件数か） */
  metric: 'uu' | 'count';
  /** 実績行の下に同じ外枠カード内で描画する追加コンテンツ（ファネルチャート等） */
  children?: ReactNode;
}

/** 前期比インジケーター（↑3 / ↓5 / 表示なし） */
function DeltaIndicator({ delta }: { delta: number }) {
  if (delta > 0) {
    return (
      <span className="text-sm leading-normal font-bold whitespace-nowrap text-primary">
        ↑{delta}
      </span>
    );
  }
  if (delta < 0) {
    return (
      <span className="text-sm leading-normal font-bold whitespace-nowrap text-[#fa0000]">
        ↓{Math.abs(delta)}
      </span>
    );
  }
  return null;
}

/**
 * 分析画面の実績サマリー
 * - 大外枠カードの中に、実績カード（primary縦線・shadow）と表示切替ボタンを並列配置
 * - API のステップ別 summary を UI ステップ（書類選考〜4次選考は「選考」へ集約）に変換して表示
 * - 表示切替はボタン直下のポップオーバー（Figma 準拠、即時反映、最低1選択必須）
 */
export default function AnalysisSummaryCards({
  summary,
  visibleSteps,
  onVisibleStepsChange,
  metric,
  children,
}: AnalysisSummaryCardsProps) {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false);

  // 表示する UI ステップ定義（apiKeys のうち少なくとも1つが visibleSteps に含まれていれば表示）
  const displayStepDefs = useMemo(
    () => ANALYSIS_STEPS.filter((s) => s.apiKeys.some((k) => visibleSteps.includes(k))),
    [visibleSteps]
  );

  // API のステップ別 summary を UI ステップに集約したマップ
  // 集約対象は apiKeys ∩ visibleSteps（部分的にオフのステップは含めない）
  const aggregatedSummaryMap = useMemo(() => {
    const map: Record<string, { current_value: number; previous_value: number; delta: number }> =
      {};
    ANALYSIS_STEPS.forEach((stepDef) => {
      const visibleApiKeys = stepDef.apiKeys.filter((k) => visibleSteps.includes(k));
      const items = summary.filter((s) => visibleApiKeys.includes(s.step));
      const current = items.reduce((sum, i) => sum + i.current_value, 0);
      const previous = items.reduce((sum, i) => sum + i.previous_value, 0);
      map[stepDef.key] = {
        current_value: current,
        previous_value: previous,
        delta: current - previous,
      };
    });
    return map;
  }, [summary, visibleSteps]);

  // 単位ラベル
  const unit = metric === 'uu' ? '名' : '件';

  // 表示ステップを ON/OFF 切替（最低1つは残す）
  const toggleStep = (stepKey: string) => {
    const isSelected = visibleSteps.includes(stepKey);
    const next = isSelected
      ? visibleSteps.filter((k) => k !== stepKey)
      : [...visibleSteps, stepKey];
    if (next.length > 0) {
      onVisibleStepsChange(next);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded border border-solid border-border bg-white p-3">
      <div className="flex items-end justify-between gap-2">
        {/* 実績カード本体（左端の primary 縦線・shadow）。Figma 準拠でコンテンツ幅にフィットさせ、伸ばさない */}
        <div className="min-w-0 overflow-x-auto rounded border-l-4 border-solid border-primary bg-white px-8 py-3 shadow-[0px_0px_2px_0px_rgba(0,0,0,0.2)]">
          <h2 className="mb-2 text-lg leading-normal font-bold text-main">実績</h2>
          <div className="flex items-stretch gap-2">
            {displayStepDefs.map((step, index) => {
              const data = aggregatedSummaryMap[step.key];
              const value = data?.current_value ?? 0;
              const delta = data?.delta ?? 0;
              const isLast = index === displayStepDefs.length - 1;
              return (
                <div
                  key={step.key}
                  className={clsx(
                    // 左寄せ・内容幅で並べ、右パディングの先に縦線（border-r）を置く（Figma 準拠）
                    'flex shrink-0 flex-col items-start justify-center pr-3',
                    !isLast && 'border-r border-solid border-border'
                  )}
                >
                  <p className="text-xs leading-[1.7] whitespace-nowrap text-sub">{step.label}</p>
                  <div className="flex items-end gap-1 font-bold">
                    <span className="min-w-[55px] text-2xl leading-[1.4] whitespace-nowrap text-main">
                      {value.toLocaleString()}
                      {unit}
                    </span>
                    <DeltaIndicator delta={delta} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>

        {/* 表示切替（ボタン直下のポップオーバー、Figma 準拠） */}
        <Popover open={isPopoverOpen} onOpenChange={setIsPopoverOpen}>
          <PopoverTrigger asChild>
            <button
              type="button"
              className={clsx(
                'flex h-[42px] shrink-0 items-center gap-1 rounded bg-white px-2 text-sm hover:bg-gray-50',
                isPopoverOpen
                  ? 'border-2 border-solid border-primary text-primary'
                  : 'border border-solid border-border text-main'
              )}
            >
              <MdVisibility
                className={clsx('size-5', isPopoverOpen ? 'text-primary' : 'text-placeholder')}
              />
              表示切替
            </button>
          </PopoverTrigger>
          <PopoverContent
            align="end"
            sideOffset={4}
            className="w-[180px] rounded p-0 shadow-[0px_0px_12px_0px_rgba(0,0,0,0.2)]"
          >
            <ul>
              {TABLE_STEPS.map((step) => {
                const isSelected = visibleSteps.includes(step.key);
                return (
                  <li key={step.key}>
                    <button
                      type="button"
                      onClick={() => toggleStep(step.key)}
                      className="flex h-10 w-full items-center justify-between px-3 py-2 text-left text-sm leading-[1.7] text-main hover:bg-gray-50"
                    >
                      <span>{step.label}</span>
                      {isSelected && <LuCircleCheckBig className="size-4 text-primary" />}
                    </button>
                  </li>
                );
              })}
            </ul>
          </PopoverContent>
        </Popover>
      </div>
      {children}
    </div>
  );
}
